/**
 * R232q5q21 ServerNpcDialog — server-driven NPC dialogue tree renderer.
 *
 * Replaces the previous client-only "gossip menu built from NpcView.npcType"
 * pipeline with the canonical server-authoritative flow:
 *
 *   1. Click NPC → window event `aiwari-server-npc-open` { npcId }.
 *   2. Component sends `npc-interact { npcId }` to the room.
 *   3. Server responds with `npc-interact-result` carrying a DialogueResult:
 *        { ok: true, next: DialogueNode | null, sideEffect?: ... }
 *      or { ok: false, reason: string }.
 *   4. We render `next.text` + `next.options[]` as parchment-scroll UI.
 *   5. Each option click → `npc-dialogue-choose { npcId, optionId }`.
 *      Server replies on `npc-dialogue-result` with the same shape.
 *   6. `sideEffect.kind` is dispatched as the appropriate window CustomEvent
 *      so VendorShop / RepairDialog / InnkeeperDialog / FlightDialog /
 *      QuestAcceptModal etc. take over without protocol changes:
 *        open_vendor      → `aiwari-open-vendor`         { vendorId }
 *        open_repair      → `aiwari-open-repair`         { npcId }
 *        open_innkeeper   → `aiwari-open-innkeeper`      { npcId }
 *        open_flightmaster→ `aiwari-server-flight-open`  { npcId }
 *        open_bank        → `aiwari-open-bank`           { bankerId }
 *        open_trainer     → `aiwari-open-spellbook`      { trainerId, classId }
 *        accept_quest     → `aiwari-quest-accept`        { questId } + close
 *        turnin_quest     → `aiwari-quest-turnin`        { questId } + close
 *        show_quest_offer → `aiwari-quest-offer`         { questId, giverNpcId, giverName }
 *        close            → just close the dialog
 *        goto             → server already returned the new node, just rerender
 *
 * Fallback: NPCs without a registered dialogueTree return ok=false (`no-tree`
 * etc). In that case we degrade to a static gossip menu derived from
 * `NpcView.npcType` so existing zones without authored trees keep working.
 *
 * Heraldic aesthetic preserved: parchment scroll #E8DCC0 → #D4C29C, Cinzel
 * headlines, EB Garamond italic body, Spectral SC option labels, gold corner
 * ornaments, wax-seal sigil at top of header.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRoom } from '../game/realmClient.js';
import { useNet } from '../state/net-store.js';

// ──────────────────────────────────────────────────────────────────────────
// Wire types — mirrors of server `DialogueResult` from npc-system.ts.
// Keep narrow: only the fields the client renders / branches on.
// ──────────────────────────────────────────────────────────────────────────

interface DialogueOption {
  id: string;
  label: string;
  /** Optional UI hint — server may pre-mark options that are visible-but-locked. */
  disabled?: boolean;
  disabledReason?: string;
}

interface DialogueNode {
  id: string;
  text: string;
  options: DialogueOption[];
}

type SideEffectKind =
  | 'goto'
  | 'close'
  | 'open_vendor'
  | 'open_trainer'
  | 'open_bank'
  | 'open_innkeeper'
  | 'open_flightmaster'
  | 'open_repair'
  | 'accept_quest'
  | 'turnin_quest'
  | 'show_quest_offer';

interface SideEffect {
  kind: SideEffectKind;
  payload?: Record<string, unknown>;
}

interface DialogueResultOk {
  ok: true;
  next: DialogueNode | null;
  sideEffect?: SideEffect;
}

interface DialogueResultErr {
  ok: false;
  reason: string;
}

type DialogueResult = DialogueResultOk | DialogueResultErr;

// ──────────────────────────────────────────────────────────────────────────
// Quest list (used for the fallback gossip path & "Дать поручение" sublist
// when the tree-based flow does not include quest options inline).
// ──────────────────────────────────────────────────────────────────────────

interface QuestStub {
  id: string;
  title: string;
  level_min: number;
  level_max: number;
  type: string;
}

interface QuestListResult {
  quests?: QuestStub[];
}

interface ReadyTurnInQuest {
  id: string;
  title: string;
  completion_text: string;
  objective_text: string;
  rewards: { xp: number; gold_aurei?: number; crystals?: number };
}

interface QuestReadyByNpcResult {
  quests?: ReadyTurnInQuest[];
}

type View = 'tree' | 'fallback' | 'quest-list' | 'turnin-list';

// ──────────────────────────────────────────────────────────────────────────
// Static labels — RU, no franchise refs.
// ──────────────────────────────────────────────────────────────────────────

const NPC_TYPE_EMOJI: Record<string, string> = {
  questGiver: '✦',
  vendor: '⚖',
  trainer: '☉',
  innkeeper: '⌂',
  flightmaster: '✈',
  repair: '⚒',
  banker: '◈',
  stableMaster: '⌘',
};

const NPC_TYPE_RU: Record<string, string> = {
  questGiver: 'Поручитель',
  vendor: 'Торговец',
  trainer: 'Наставник',
  innkeeper: 'Содержатель Постоялого Двора',
  flightmaster: 'Хранитель Грифонов',
  repair: 'Кузнец-починщик',
  banker: 'Хранитель Сундуков',
  stableMaster: 'Смотритель Питомников',
};

const FALLBACK_GREETING_POOL = [
  'Что я могу для тебя сделать, %s?',
  'Чем могу помочь, %s?',
  'Доброго утра, путник.',
  'Что привело тебя?',
  'Слушаю.',
];

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

export function ServerNpcDialog() {
  const npcs = useNet((s) => s.npcs);
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);

  const [npcId, setNpcId] = useState<string | null>(null);
  const [view, setView] = useState<View>('tree');
  const [node, setNode] = useState<DialogueNode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Fallback-only state.
  const [quests, setQuests] = useState<QuestStub[] | null>(null);
  const [readyTurnIns, setReadyTurnIns] = useState<ReadyTurnInQuest[] | null>(null);
  const [fallbackGreeting, setFallbackGreeting] = useState<string>(
    FALLBACK_GREETING_POOL[0] ?? 'Слушаю.',
  );

  // Track current npcId in a ref so message handlers always see the latest
  // value without re-binding (Colyseus 0.16 has no offMessage).
  const npcIdRef = useRef<string | null>(null);
  useEffect(() => { npcIdRef.current = npcId; }, [npcId]);

  const npc = npcId ? npcs[npcId] : undefined;

  const playerName = useMemo(() => {
    if (!selfId) return 'путник';
    const me = players[selfId];
    return me?.name || me?.classId || 'путник';
  }, [selfId, players]);

  const close = useCallback(() => {
    setNpcId(null);
    setNode(null);
    setView('tree');
    setQuests(null);
    setReadyTurnIns(null);
    setError(null);
    setBusy(false);
  }, []);

  // ── Side-effect dispatcher ──────────────────────────────────────────────

  const dispatchSideEffect = useCallback((eff: SideEffect, currentNpcId: string) => {
    const payload = eff.payload ?? {};
    switch (eff.kind) {
      case 'open_vendor': {
        // CRITICAL: use the action's vendorId field, NOT npcId. This is what
        // fixes the vendor-routing bug — a quest-giver can hand off to a
        // separate vendor NPC via { kind: 'open_vendor', vendorId: 'vendor-…' }.
        const vendorId = (payload['vendorId'] as string | undefined) ?? currentNpcId;
        const vendorName = npcs[vendorId]?.displayName;
        window.dispatchEvent(new CustomEvent('aiwari-open-vendor', {
          detail: { vendorId, vendorName },
        }));
        close();
        break;
      }
      case 'open_trainer': {
        const trainerId = (payload['trainerId'] as string | undefined) ?? currentNpcId;
        const me = selfId ? players[selfId] : undefined;
        window.dispatchEvent(new CustomEvent('aiwari-open-spellbook', {
          detail: { trainerId, classId: me?.classId ?? '' },
        }));
        close();
        break;
      }
      case 'open_bank': {
        const bankerName = npcs[currentNpcId]?.displayName;
        window.dispatchEvent(new CustomEvent('aiwari-open-bank', {
          detail: { bankerId: currentNpcId, bankerName },
        }));
        close();
        break;
      }
      case 'open_innkeeper': {
        window.dispatchEvent(new CustomEvent('aiwari-open-innkeeper', {
          detail: { npcId: currentNpcId },
        }));
        close();
        break;
      }
      case 'open_flightmaster': {
        window.dispatchEvent(new CustomEvent('aiwari-server-flight-open', {
          detail: { npcId: currentNpcId },
        }));
        close();
        break;
      }
      case 'open_repair': {
        window.dispatchEvent(new CustomEvent('aiwari-open-repair', {
          detail: { npcId: currentNpcId },
        }));
        close();
        break;
      }
      case 'accept_quest': {
        const questId = payload['questId'] as string | undefined;
        if (questId) {
          window.dispatchEvent(new CustomEvent('aiwari-quest-accept', {
            detail: { questId },
          }));
        }
        close();
        break;
      }
      case 'turnin_quest': {
        const questId = payload['questId'] as string | undefined;
        if (questId) {
          window.dispatchEvent(new CustomEvent('aiwari-quest-turnin', {
            detail: { questId },
          }));
        }
        close();
        break;
      }
      case 'show_quest_offer': {
        const questId = payload['questId'] as string | undefined;
        if (questId) {
          const giverName = npcs[currentNpcId]?.displayName ?? '';
          window.dispatchEvent(new CustomEvent('aiwari-quest-offer', {
            detail: { questId, giverNpcId: currentNpcId, giverName },
          }));
        }
        // QuestAcceptModal layers above us; keep tree state so the user can
        // back out of the offer. The server already advanced sess.node.
        break;
      }
      case 'close': {
        close();
        break;
      }
      case 'goto':
      default: {
        // No side-effect — server already returned the new node.
        break;
      }
    }
  }, [close, selfId, players, npcs]);

  // ── Open handler — fires on `aiwari-server-npc-open` ────────────────────

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ npcId: string }>).detail;
      if (!detail?.npcId) return;
      // R232q86zdf: Northshire агент регистрирует canonical NPC с id
      // 'dbc-npc-XXXXX' (vanilla-npc-promoter.ts). Старый фильтр требовал
      // 'npc-' prefix → F-диалог не открывался → юзер видит [F]
      // ЗАГОВОРИТЬ но ничего не происходит. Принимаем оба формата.
      if (!detail.npcId.startsWith('npc-') && !detail.npcId.startsWith('dbc-npc-')) return;
      const room = getRoom();
      setNpcId(detail.npcId);
      setView('tree');
      setNode(null);
      setQuests(null);
      setReadyTurnIns(null);
      setError(null);
      setBusy(true);
      const idx = Math.floor(Math.random() * FALLBACK_GREETING_POOL.length);
      setFallbackGreeting(FALLBACK_GREETING_POOL[idx] ?? 'Слушаю.');
      if (!room) {
        setBusy(false);
        setError('Связь со Стормвейлом потеряна.');
        return;
      }
      room.send('npc-interact', { npcId: detail.npcId });
    };
    window.addEventListener('aiwari-server-npc-open', handler as EventListener);
    return () => window.removeEventListener('aiwari-server-npc-open', handler as EventListener);
  }, []);

  // ── Server message subscriptions ────────────────────────────────────────
  // R232q86zdg: ServerNpcDialog mounts at app startup (before any room joins),
  // so getRoom() returns null on first effect run. We poll every 500ms until
  // a room is available, then bind. Without this re-bind the F-prompt sent
  // 'npc-interact' but the result handler never registered → "Свитки
  // разворачиваются..." forever.
  const [roomReady, setRoomReady] = useState(0);
  useEffect(() => {
    if (getRoom()) { setRoomReady((n) => n + 1); return; }
    const t = window.setInterval(() => {
      if (getRoom()) { setRoomReady((n) => n + 1); window.clearInterval(t); }
    }, 500);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    const room = getRoom();
    if (!room) return;

    const onResult = (msg: DialogueResult) => {
      // Ignore stale messages from a previous session.
      if (!npcIdRef.current) return;
      setBusy(false);
      if (!msg) return;
      if (msg.ok === false) {
        // Tree-less or invalid NPC → fall back to client gossip menu.
        // 'no-tree' / 'unknown-npc' / 'out-of-range' / 'hostile-faction' /
        // 'no-session' — any of these mean we can't drive the tree path.
        if (msg.reason === 'no-tree' || msg.reason === 'no-session') {
          setView('fallback');
          setNode(null);
          setError(null);
          return;
        }
        // Real error — surface it but stay open so the user can close.
        setError(reasonRu(msg.reason));
        return;
      }
      // ok = true.
      if (msg.sideEffect) {
        const id = npcIdRef.current;
        if (id) dispatchSideEffect(msg.sideEffect, id);
      }
      // Some side effects close the dialog; otherwise render the new node.
      if (msg.next) {
        setNode(msg.next);
        setView('tree');
        setError(null);
      } else if (!msg.sideEffect || msg.sideEffect.kind === 'close') {
        // Tree ended without a side-effect → close.
        if (!msg.sideEffect) close();
      }
    };

    const onQuestList = (msg: QuestListResult) => {
      if (!npcIdRef.current) return;
      setBusy(false);
      setQuests(msg.quests ?? []);
    };
    const onReady = (msg: QuestReadyByNpcResult) => {
      if (!npcIdRef.current) return;
      setBusy(false);
      setReadyTurnIns(msg.quests ?? []);
    };

    room.onMessage('npc-interact-result', onResult);
    room.onMessage('npc-dialogue-result', onResult);
    room.onMessage('quest-list-by-giver-result', onQuestList);
    room.onMessage('quest-ready-by-npc-result', onReady);
    return () => { /* Colyseus 0.16 has no offMessage — bound for room lifetime */ };
    // R232q86zdg: depend on roomReady so we rebind when the room arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomReady]);

  // ── Esc handling ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!npcId) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (view === 'quest-list') { setView('fallback'); setQuests(null); setError(null); return; }
      if (view === 'turnin-list') { setView('fallback'); setReadyTurnIns(null); setError(null); return; }
      close();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [npcId, view, close]);

  if (!npcId || !npc) return null;

  const typeRu = NPC_TYPE_RU[npc.npcType] ?? 'Странник';
  const typeEmoji = NPC_TYPE_EMOJI[npc.npcType] ?? '◇';

  // ── Option click — server-driven ────────────────────────────────────────

  const onOption = (optionId: string) => {
    const room = getRoom();
    if (!room || !npcId) return;
    setBusy(true);
    setError(null);
    room.send('npc-dialogue-choose', { npcId, optionId });
  };

  // ── Fallback gossip handlers (NPCs without dialogueTree) ────────────────

  const isQuestGiver   = npc.npcType === 'questGiver';
  const isTrainer      = npc.npcType === 'trainer';
  const isVendor       = npc.npcType === 'vendor';
  const isInnkeeper    = npc.npcType === 'innkeeper';
  const isFlightMaster = npc.npcType === 'flightmaster';
  const isBanker       = npc.npcType === 'banker';
  const isRepair       = npc.npcType === 'repair';
  const isStableMaster = npc.npcType === 'stableMaster';
  const hasMarkerHint = !!npc.questMarker && (npc.questMarker.includes('!') || npc.questMarker.includes('?'));

  const fallbackGreetingText = fallbackGreeting.replace('%s', playerName);

  const onFallbackQuestGive = () => {
    const room = getRoom();
    if (!room) { setError('Связь со Стормвейлом потеряна.'); return; }
    setBusy(true);
    setView('quest-list');
    setQuests(null);
    setError(null);
    room.send('quest-list-by-giver', { giverNpc: npcId });
  };
  const onFallbackTurnInView = () => {
    const room = getRoom();
    if (!room) { setError('Связь со Стормвейлом потеряна.'); return; }
    setBusy(true);
    setView('turnin-list');
    setReadyTurnIns(null);
    setError(null);
    room.send('quest-ready-by-npc', { npcId });
  };
  const onFallbackTurnIn = (questId: string) => {
    window.dispatchEvent(new CustomEvent('aiwari-quest-turnin', { detail: { questId } }));
    close();
  };
  const onFallbackTrainer = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-spellbook', {
      detail: { trainerId: npcId, classId: players[selfId ?? '']?.classId ?? '' },
    }));
    close();
  };
  const onFallbackVendor = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-vendor', {
      detail: { vendorId: npcId, vendorName: npc.displayName },
    }));
    close();
  };
  const onFallbackInn = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-innkeeper', {
      detail: {
        npcId,
        innId: npcId,
        innName: npc.displayName,
        pos: npc.pos,
        faction: npc.faction,
      },
    }));
    close();
  };
  const onFallbackFlights = () => {
    window.dispatchEvent(new CustomEvent('aiwari-server-flight-open', {
      detail: { npcId },
    }));
    close();
  };
  const onFallbackBank = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-bank', {
      detail: { bankerId: npcId, bankerName: npc.displayName },
    }));
    close();
  };
  const onFallbackRepair = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-repair', {
      detail: { npcId, repairId: npcId, repairName: npc.displayName },
    }));
    close();
  };
  const onFallbackStable = () => {
    window.dispatchEvent(new CustomEvent('aiwari-open-stable-master', {
      detail: { id: npcId },
    }));
    close();
  };
  const onFallbackPickQuest = (questId: string) => {
    window.dispatchEvent(new CustomEvent('aiwari-quest-offer', {
      detail: { questId, giverNpcId: npcId, giverName: npc.displayName },
    }));
    close();
  };

  const fallbackQuestCountLabel = (() => {
    if (Array.isArray(quests)) return `[${quests.length}]`;
    if (hasMarkerHint && npc.questMarker?.includes('!')) return '[!]';
    return '';
  })();

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-6"
      style={{
        background: 'radial-gradient(circle at center, rgba(11,9,7,0.78) 0%, rgba(0,0,0,0.92) 90%)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="relative max-w-[680px] w-[92vw] max-h-[80vh] flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #E8DCC0 0%, #D4C29C 100%)',
          border: '3px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.3), inset 0 0 40px rgba(92,69,32,0.18)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#3A2818',
        }}
      >
        <Corner pos="tl" />
        <Corner pos="tr" />
        <Corner pos="bl" />
        <Corner pos="br" />

        {/* Header — wax-seal sigil + name + type */}
        <div
          className="flex items-center gap-4 px-8 pt-8 pb-4 border-b"
          style={{ borderColor: '#8A6B2E' }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #C9A96A, #8A6B2E)',
              border: '3px solid #3A2818',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 -3px 6px rgba(0,0,0,0.3)',
              color: '#0B0907',
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
            }}
            aria-hidden
          >
            {typeEmoji}
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className="text-[28px] truncate"
              style={{
                fontFamily: 'Cinzel, serif',
                fontWeight: 700,
                color: '#3A2818',
                letterSpacing: '0.02em',
              }}
            >
              {npc.displayName}
            </h2>
            <div
              className="text-[12px] uppercase tracking-[0.2em] mt-0.5"
              style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
            >
              {typeRu}
            </div>
            {npc.subtitle && (
              <div className="text-[13px] italic mt-1" style={{ color: '#5C4520' }}>
                {npc.subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-[28px] px-2 leading-none hover:scale-110 transition"
            style={{ color: '#5C4520' }}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 overflow-y-auto flex-1">
          {error && (
            <div
              className="text-center text-[15px] italic px-4 py-3 mb-4"
              style={{
                background: 'rgba(163, 50, 50, 0.12)',
                color: '#A33232',
                border: '1px solid #A33232',
              }}
            >
              {error}
            </div>
          )}

          {busy && view === 'tree' && !node && (
            <div
              className="text-center text-[14px] italic py-6"
              style={{ color: '#8A6B2E', fontFamily: 'EB Garamond, serif' }}
            >
              Свитки разворачиваются…
            </div>
          )}

          {/* Server-driven dialogue tree view */}
          {view === 'tree' && node && (
            <>
              <div
                className="text-[17px] leading-relaxed italic mb-6 whitespace-pre-line"
                style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
              >
                «{node.text}»
              </div>
              <div className="flex flex-col gap-2">
                {node.options.length === 0 && (
                  <GossipOption icon="›" label="До свидания" onClick={close} muted />
                )}
                {node.options.map((opt) => (
                  <GossipOption
                    key={opt.id}
                    icon="◇"
                    label={opt.label}
                    onClick={() => !opt.disabled && onOption(opt.id)}
                    disabled={!!opt.disabled}
                    hint={opt.disabledReason}
                  />
                ))}
              </div>
            </>
          )}

          {/* Fallback gossip view — only when server returned no-tree. */}
          {view === 'fallback' && (
            <>
              <div
                className="text-[17px] leading-relaxed italic mb-6"
                style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
              >
                «{fallbackGreetingText}»
              </div>
              <div className="flex flex-col gap-2">
                {isTrainer && (
                  <GossipOption icon="☉" label="Учиться у меня" onClick={onFallbackTrainer} />
                )}
                {isQuestGiver && npc.questMarker?.includes('?') && (
                  <GossipOption icon="?" label="Сдать задание" onClick={onFallbackTurnInView} />
                )}
                {isQuestGiver && (
                  <GossipOption
                    icon="✦"
                    label={`Дать поручение ${fallbackQuestCountLabel}`.trim()}
                    onClick={onFallbackQuestGive}
                  />
                )}
                {isVendor && (
                  <GossipOption icon="⚖" label="Я хочу торговать" onClick={onFallbackVendor} />
                )}
                {isInnkeeper && (
                  <>
                    <GossipOption icon="⌂" label="Я хочу остановиться" onClick={onFallbackInn} />
                    <GossipOption icon="◈" label="Установить точку привязки" onClick={onFallbackInn} />
                  </>
                )}
                {isFlightMaster && (
                  <GossipOption icon="✈" label="Покажи маршруты" onClick={onFallbackFlights} />
                )}
                {isBanker && (
                  <GossipOption icon="◈" label="Открой мой банк" onClick={onFallbackBank} />
                )}
                {isRepair && (
                  <GossipOption icon="⚒" label="Почини моё снаряжение" onClick={onFallbackRepair} />
                )}
                {isStableMaster && (
                  <GossipOption icon="⌘" label="Питомцы на хранении" onClick={onFallbackStable} />
                )}
                <div style={{ height: 6 }} />
                <GossipOption icon="›" label="До свидания" onClick={close} muted />
              </div>
            </>
          )}

          {view === 'quest-list' && (
            <>
              <button
                type="button"
                onClick={() => { setView('fallback'); setQuests(null); }}
                className="text-[12px] mb-3 hover:underline"
                style={{ color: '#5C4520', fontFamily: 'Spectral SC, serif', letterSpacing: '0.08em' }}
              >
                ← Назад
              </button>
              <div
                className="text-[10px] uppercase tracking-[0.32em] mb-4"
                style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
              >
                Доступные поручения
              </div>
              {busy && (
                <div className="text-center text-[14px] italic" style={{ color: '#8A6B2E' }}>
                  Свитки разворачиваются…
                </div>
              )}
              {!busy && Array.isArray(quests) && quests.length === 0 && (
                <div
                  className="text-[15px] italic text-center px-4 py-6"
                  style={{ color: '#5C4520', fontFamily: 'EB Garamond, serif' }}
                >
                  «Сейчас у меня нет работы для тебя. Возвращайся позже.»
                </div>
              )}
              {!busy && Array.isArray(quests) && quests.length > 0 && (
                <div className="flex flex-col gap-2">
                  {quests.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => onFallbackPickQuest(q.id)}
                      className="text-left px-4 py-3 transition hover:bg-[rgba(201,169,106,0.18)]"
                      style={{
                        border: '1px solid #5C4520',
                        background: 'rgba(232,220,192,0.4)',
                        color: '#3A2818',
                        fontFamily: 'Spectral SC, serif',
                        letterSpacing: '0.04em',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ color: '#E6B800', marginRight: 8, fontWeight: 700 }}>!</span>
                      <span style={{ fontWeight: 600 }}>{q.title}</span>
                      <span
                        style={{ marginLeft: 10, fontSize: 11, color: '#8A6B2E', fontStyle: 'italic' }}
                      >
                        ур. {q.level_min}
                        {q.level_max && q.level_max !== q.level_min ? `–${q.level_max}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {view === 'turnin-list' && (
            <>
              <button
                type="button"
                onClick={() => { setView('fallback'); setReadyTurnIns(null); }}
                className="text-[12px] mb-3 hover:underline"
                style={{ color: '#5C4520', fontFamily: 'Spectral SC, serif', letterSpacing: '0.08em' }}
              >
                ← Назад
              </button>
              <div
                className="text-[10px] uppercase tracking-[0.32em] mb-4"
                style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
              >
                Готово к сдаче
              </div>
              {busy && (
                <div className="text-center text-[14px] italic" style={{ color: '#8A6B2E' }}>
                  Проверяем записи…
                </div>
              )}
              {!busy && Array.isArray(readyTurnIns) && readyTurnIns.length === 0 && (
                <div
                  className="text-[15px] italic text-center px-4 py-6"
                  style={{ color: '#5C4520', fontFamily: 'EB Garamond, serif' }}
                >
                  «Пока нечего сдавать.»
                </div>
              )}
              {!busy && Array.isArray(readyTurnIns) && readyTurnIns.length > 0 && (
                <div className="flex flex-col gap-3">
                  {readyTurnIns.map((q) => (
                    <div
                      key={q.id}
                      style={{
                        border: '1px solid #5BAE57',
                        background: 'rgba(91,174,87,0.08)',
                        padding: '12px 16px',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'Spectral SC, serif',
                          fontWeight: 600,
                          color: '#3A2818',
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ color: '#5BAE57', marginRight: 8 }}>✓</span>
                        {q.title}
                      </div>
                      <div
                        className="text-[13px] italic mb-3"
                        style={{ color: '#5C4520', fontFamily: 'EB Garamond, serif' }}
                      >
                        «{q.completion_text}»
                      </div>
                      <div className="flex gap-2 flex-wrap mb-3">
                        {q.rewards.xp > 0 && (
                          <span style={{ fontSize: 11, color: '#8A6B2E', border: '1px solid #8A6B2E', padding: '2px 8px', fontFamily: 'Spectral SC, serif' }}>
                            {q.rewards.xp} опыта
                          </span>
                        )}
                        {q.rewards.gold_aurei && q.rewards.gold_aurei > 0 && (
                          <span style={{ fontSize: 11, color: '#C9A96A', border: '1px solid #C9A96A', padding: '2px 8px', fontFamily: 'Spectral SC, serif' }}>
                            {q.rewards.gold_aurei} ауреев
                          </span>
                        )}
                        {q.rewards.crystals && q.rewards.crystals > 0 && (
                          <span style={{ fontSize: 11, color: '#3E6FA5', border: '1px solid #3E6FA5', padding: '2px 8px', fontFamily: 'Spectral SC, serif' }}>
                            {q.rewards.crystals} кристаллов
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onFallbackTurnIn(q.id)}
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.15em] hover:scale-[1.02] transition"
                        style={{
                          background: 'linear-gradient(180deg, #5BAE57 0%, #3A8A36 100%)',
                          color: '#fff',
                          border: '1px solid #2A6A26',
                          fontFamily: 'Cinzel, serif',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Сдать задание
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-8 py-3 text-center text-[10px] uppercase tracking-[0.3em]"
          style={{
            background: 'rgba(58,40,24,0.06)',
            borderTop: '1px solid #8A6B2E',
            fontFamily: 'Spectral SC, serif',
            color: '#9C8E72',
          }}
        >
          Esc · {view === 'quest-list' || view === 'turnin-list' ? 'Назад' : 'Уйти'}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function reasonRu(reason: string): string {
  switch (reason) {
    case 'dead':                return 'Ты не можешь говорить — твоя душа далеко.';
    case 'unknown-npc':         return 'Этого человека уже нет здесь.';
    case 'out-of-range':        return 'Подойди ближе.';
    case 'hostile-faction':     return 'Он не желает говорить с тобой.';
    case 'no-tree':             return 'Этот человек хранит молчание.';
    case 'no-node':             return 'Свиток беседы потерян.';
    case 'no-session':          return 'Беседа прервалась — начни снова.';
    case 'unknown-option':      return 'Этот ответ ему незнаком.';
    case 'requirement-failed':  return 'Ты пока недостоин этого ответа.';
    case 'goto-missing':        return 'Дальнейшая речь утрачена.';
    case 'unknown-action':      return 'Этот выбор не имеет смысла.';
    default:                    return `Сбой: ${reason}`;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────────────────

function GossipOption({
  icon,
  label,
  onClick,
  muted = false,
  disabled = false,
  hint,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  muted?: boolean;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? hint : undefined}
      className="text-left px-4 py-3 transition hover:bg-[rgba(201,169,106,0.18)]"
      style={{
        border: '1px solid #5C4520',
        background: muted ? 'rgba(212,194,156,0.25)' : 'rgba(232,220,192,0.4)',
        color: '#3A2818',
        fontFamily: 'Spectral SC, serif',
        letterSpacing: '0.04em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : (muted ? 0.85 : 1),
      }}
    >
      <span style={{ color: '#8A6B2E', marginRight: 10, fontWeight: 700 }}>{icon}</span>
      {label}
      {disabled && hint && (
        <span style={{ marginLeft: 8, fontSize: 11, fontStyle: 'italic', color: '#A33232' }}>
          ({hint})
        </span>
      )}
    </button>
  );
}

function Corner({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const cls: Record<typeof pos, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${cls[pos]} w-7 h-7 pointer-events-none`}
      viewBox="0 0 28 28"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L10 2 M2 2 L2 10" />
      <path d="M3 3 Q9 3 9 9" />
      <circle cx="5" cy="5" r="1" fill="#5C4520" />
    </svg>
  );
}
